/**
 * Строка хранилища — один отчёт в списке.
 *
 * Читается слева направо как предложение: какой период, что там с баллом,
 * сколько денег стояло под риском, в каком состоянии документ. Клик по всей
 * строке, а не по кнопке в конце: цель шириной в карточку промахнуться не
 * даёт, а отдельная кнопка «Открыть» в каждой строке — шесть одинаковых
 * кнопок на экране.
 *
 * Корпус берём у отчёта — `SplitPanel`: строки склеиваются в одну группу
 * швом в пиксель, как любой список внутри документа. Несобранный отчёт
 * строкой остаётся, но кликом не открывается — панель без обработчика
 * рендерится обычным блоком, и кнопки, которая никуда не ведёт, в разметке
 * не заводится.
 *
 * Числа набирает `Metric` — тот же компонент, что и на витрине, и внутри
 * самого отчёта, только ступенью мельче.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Icon} from 'ui/components/atoms/Icon';
import {Indicator} from 'ui/components/atoms/Indicator';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {Metric} from '../../ExecutiveReport/components/Metric';
import {SplitPanel} from '../../ExecutiveReport/components/SplitCard';
import {useReportFormat} from '../../ExecutiveReport/format';
import {MEASURE, longDate, reportPeriodTitle, signed} from '../../ExecutiveReport/helpers';
import {deltaTone, scoreTone, statusMeta} from '../helpers';
import type {ReportEntry} from '../types';

// NBSP в JSX-тексте запрещён линтером — типографированную подпись держим
// строкой.
const SCORE_LABEL = 'Оценка';
const RISK_LABEL = 'Под риском';

/**
 * Ширина числовой колонки — четыре верхних шага шкалы отступов.
 *
 * Колонка держит одинаковую ширину во всех строках, иначе суммы разной
 * длины разъезжаются и список перестаёт просматриваться столбиком. Значение
 * взято от шкалы, а не выдумано: колонка обязана вмещать самую длинную
 * сумму, набранную кеглем `Metric` ступени `m`.
 */
const NUMBER_COLUMN = spacingPx('xl') * 4;

export interface ReportRowProps {
  entry: ReportEntry;
  /** Открыть отчёт. Не передан — строка остаётся некликабельной. */
  onOpen?: (id: string) => void;
}

const Period = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
});

const Sub = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

/** Числовая колонка фиксированной ширины — см. `NUMBER_COLUMN`. */
const Cell = styled(Box)({
  minWidth: NUMBER_COLUMN,
});

/**
 * Дельта балла стоит единицей измерения рядом с самим баллом: слот `unit`
 * ставит её на общую с числом базовую линию. Собственный цвет здесь нужен —
 * `unit` приглушён, а рост и падение обязаны отличаться.
 */
const Delta = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: 'good' | 'alert' | 'bad' | 'neutral'}>(({theme, $tone}) => ({
  color: $tone === 'neutral' ? surfaceVar('onSurfaceMuted') : theme.palette[$tone].main,
}));

const Note = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

export function ReportRow({entry, onOpen}: ReportRowProps) {
  // Валюта у сумм одна на весь отчётный дом и живёт в `format.ts`. Голый
  // `moneyParts` без неё роняет строку: `Intl.NumberFormat` со `style:
  // 'currency'` требует код валюты и бросает, если его нет.
  const fmt = useReportFormat();
  const status = statusMeta(entry.status);
  // Заголовок строки — из границ периода: целый месяц называется по имени,
  // обрезанный остаётся диапазоном.
  const periodLabel = reportPeriodTitle(entry.periodFrom, entry.periodTo);
  const delta =
    entry.score != null && entry.scorePrev != null ? entry.score - entry.scorePrev : null;
  const risk = entry.atRisk != null ? fmt.moneyParts(entry.atRisk) : null;
  const clickable = entry.status === 'ready' && onOpen != null;

  return (
    <SplitPanel
      onClick={clickable ? () => onOpen(entry.id) : undefined}
      ariaLabel={clickable ? `Открыть отчёт за ${periodLabel}` : undefined}
    >
      <Row gap='m' alignY='center' justify='between' reflowAt='md'>
        <Stack gap='xs'>
          <Row gap='xs' alignY='center'>
            {entry.unread === true && <Indicator kind='dot' size='nano' tone='primary' />}
            <Period>{periodLabel}</Period>
          </Row>
          {entry.date != null && (
            <Sub>
              {status.dateLabel} {longDate(entry.date)}
            </Sub>
          )}
        </Stack>

        {/* Ряд не отдаёт ширину соседнему `Stack`: пилюля статуса не
            переносится по словам, и на сжатии её подпись уходила в
            многоточие («Запланирован» → «Заплани…»). */}
        <Row gap='l' alignY='center' wrap='wrap' width='hug'>
          {entry.score != null && (
            <Cell>
              <Metric
                size='m'
                label={SCORE_LABEL}
                value={entry.score}
                tone={scoreTone(entry.score)}
                unit={
                  delta != null && delta !== 0 ? (
                    <Delta as='span' $tone={deltaTone(delta)}>
                      {signed(delta)}
                    </Delta>
                  ) : undefined
                }
              />
            </Cell>
          )}

          {risk != null && (
            <Cell>
              <Metric size='m' label={RISK_LABEL} value={risk.value} unit={risk.unit} />
            </Cell>
          )}

          <ChipMUI mode='badge' color={status.tone} variant='soft' size='s' leftIcon={status.icon}>
            {status.label}
          </ChipMUI>

          {clickable && <Icon name='chevron_right' size='s' color='on-surface-muted' />}
        </Row>
      </Row>

      {entry.note != null && <Note>{entry.note}</Note>}
    </SplitPanel>
  );
}
